import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useQueryParams } from 'use-query-params'
import _ from 'lodash'
import { CategoryListQueryResult, useCategoryListQuery } from '@graphql/queries/__generated__/category.generated'
import { CartDocument, CartLinesQueryResult, useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { CartTypeEnum } from '../../../../generated/gqlTypes'
import { useCartUpdateProductVariantMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import { useCartClearMutation } from '@graphql/mutations/__generated__/cart.generated'
import { useAddFavoritesProductMutation } from '@graphql/mutations/__generated__/me.generated'
import Loader from '@components/Loader'
import CartLinesCategory from './CartLinesCategory'
import { CartParams } from '../types'
import { DataLayerItem, googlePushProductRemoveFromCart } from '../../../utils/dataLayer'

type CartLineNode = CartLinesQueryResult['data']['cartLines']['results'][0]

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface Props {
  isAuthenticated: boolean
  lineLoading: number | null

  setLineLoading(pk: number | null): void

  onClickProduct(specId: string | number, slug: string): void
}

const CartLinesContainer: React.FC<Props> = ({ isAuthenticated, lineLoading, setLineLoading, onClickProduct }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [, setParams] = useQueryParams(CartParams)

  const categoryListQueryResult = useCategoryListQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
  })

  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
      },
      'CartShippingDiscounts',
    ],
  })
  const [cartClear] = useCartClearMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
      },
    ],
  })
  const [addToFavorites] = useAddFavoritesProductMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesCategoriesInfo'],
  })

  const handleEditQty = async (line: CartLineNode, qty: number) => {
    setLineLoading(line.pk)
    try {
      const variant = line.variant
      await cartUpdateVariant({
        variables: {
          variant: variant.pk,
          cartLine: line.pk,
          quantity: qty,
          cartType: CartTypeEnum.Default,
        },
      })
      const price = !line.supplyParts.length
        ? line.unitPrice.discount
        : _.sumBy(line.supplyParts, part => part.unitPrice.discount * part.quantity)
      const items: DataLayerItem[] = [
        {
          item_id: variant.sku,
          item_name: line.variant.product.siteSpecifics[0]?.name,
          index: 0,
          item_variant: variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`).join(),
          quantity: line.quantity,
          price,
        },
      ]
      googlePushProductRemoveFromCart({ currency: line.unitPrice.currency, value: price * line.quantity, items })
      enqueueSnackbar(
        t('snackbarMessages.success.shoppingBagModified', 'Your shopping bag has been modified successfully!'),
        {
          variant: 'success',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const handleRemoveLine = async (line: CartLineNode) => {
    await handleEditQty(line, 0)
  }

  const handleSaveForLaterLine = async (productPk: number, line: CartLineNode) => {
    setLineLoading(line.pk)
    try {
      await addToFavorites({
        variables: {
          pk: productPk,
        },
      })
      await handleRemoveLine(line)
      enqueueSnackbar(t('snackbarMessages.success.productAddedToFavorites', 'Product has been added to favorites.'), {
        variant: 'success',
      })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const handleEditLine = async (linePk: number) => {
    setParams({ editLinePk: linePk }, 'replaceIn')
  }

  const handleClearCategory = async (categoryId: string) => {
    setLineLoading(-1)
    try {
      await cartClear({ variables: { cartType: CartTypeEnum.Default, category: categoryId } })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const categories = categoryListQueryResult.data?.categories || []
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  const sortedCategories: CategoryListQueryResult['data']['categories'] = categories.filter(cat =>
    Object.keys(categoriesWithLines).includes(cat.pk.toString())
  )

  return (
    <div>
      {!sortedCategories.length && (
        <div className='bg-white shadow-card rounded-lg relative'>
          <div className='py-40 text-center text-lg relative'>
            <Loader
              className='spinner spinner--small'
              type='ThreeDots'
              color='#333'
              visible={cartQueryResult.loading}
              height={100}
              width={100}
            />
            {!cartQueryResult.loading && <span>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>}
          </div>
        </div>
      )}
      {sortedCategories.map((cat, idx) => (
        <CartLinesCategory
          key={cat.pk}
          category={cat}
          lineLoading={lineLoading}
          isAuthenticated={isAuthenticated}
          onClickProduct={onClickProduct}
          handleEditLine={handleEditLine}
          handleRemoveLine={handleRemoveLine}
          handleEditQty={handleEditQty}
          handleSaveForLaterLine={handleSaveForLaterLine}
          initialize={idx === 0}
          cartClear={handleClearCategory}
          categoryLinesInfo={categoriesWithLines[cat.pk] || []}
        />
      ))}
    </div>
  )
}

export default CartLinesContainer
